import { useEffect, useState, useRef, useCallback } from 'react'

// Constants
const MEMORY_THRESHOLDS = {
  CRITICAL: 800,
  WARNING: 500,
  HIGH: 600,
  MEDIUM: 450,
}

const COLORS = {
  CRITICAL: { bg: '#ffebee', text: '#c62828', border: '#ef5350' },
  WARNING: { bg: '#fff3e0', text: '#ef6c00', border: '#ff9800' },
  NORMAL: { bg: '#e8f5e9', text: '#2e7d32', border: '#66bb6a' },
  REDUX_WARNING: { bg: '#e3f2fd', text: '#1976d2', border: '#1976d2' },
}

const MAX_ALERTS = 100
const CHECK_INTERVAL = 3000
const REDUX_CHECK_INTERVAL = 5000

const MemoryMonitor = ({ store }) => {
  const [memoryStats, setMemoryStats] = useState(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [alerts, setAlerts] = useState([])
  const [reduxSize, setReduxSize] = useState(0)
  const [position, setPosition] = useState({
    x: typeof window !== 'undefined' ? window.innerWidth / 2 - 100 : 0,
    y: typeof window !== 'undefined' ? window.innerHeight - 100 : 0,
  })
  const [isDragging, setIsDragging] = useState(false)
  const [isAllowed, setIsAllowed] = useState(false)

  const dragOffset = useRef({ x: 0, y: 0 })
  const lastReduxCheck = useRef(Date.now())
  const memoryCheckInterval = useRef(null)

  // Check permission from localStorage
  useEffect(() => {
    const config = localStorage.getItem('biziisconfig')
    if (config === 'InstaBizConfig') {
      setIsAllowed(true)
    } else {
      setIsAllowed(false)
    }
  }, [])

  // Calculate Redux store size with memoization
  const calculateReduxSize = useCallback((state) => {
    try {
      const serialized = JSON.stringify(state)
      const sizeInBytes = new Blob([serialized]).size
      return parseFloat((sizeInBytes / 1048576).toFixed(2))
    } catch (error) {
      console.error('Failed to calculate Redux size:', error)
      return 0
    }
  }, [])

  // Add alert with deduplication
  const addAlert = useCallback((alertEntry) => {
    setAlerts((prev) => {
      const exists = prev.some(
        (a) => a.url === alertEntry.url && Math.abs(a.memory - alertEntry.memory) < 5
      )
      if (!exists) {
        return [alertEntry, ...prev].slice(0, MAX_ALERTS)
      }
      return prev
    })
  }, [])

  // Check memory usage
  const checkMemory = useCallback(() => {
    if (typeof performance === 'undefined' || !performance.memory) return

    try {
      const memory = performance.memory
      const used = parseFloat((memory.usedJSHeapSize / 1048576).toFixed(2))
      const total = parseFloat((memory.totalJSHeapSize / 1048576).toFixed(2))
      const limit = parseFloat((memory.jsHeapSizeLimit / 1048576).toFixed(2))
      const currentUrl =
        typeof window !== 'undefined'
          ? window.location.pathname + window.location.search
          : ''

      // Only check Redux size periodically to reduce overhead
      let currentReduxSize = reduxSize
      const now = Date.now()
      if (store && now - lastReduxCheck.current > REDUX_CHECK_INTERVAL) {
        currentReduxSize = calculateReduxSize(store.getState())
        setReduxSize(currentReduxSize)
        lastReduxCheck.current = now
      }

      const stats = {
        used,
        total,
        limit,
        reduxSize: currentReduxSize,
        url: currentUrl,
        timestamp: new Date().toLocaleString(),
      }

      setMemoryStats(stats)

      // Save to localStorage for reference
      localStorage.setItem('memoryMonitorStats', JSON.stringify(stats))

      // Add alert if memory usage is high
      if (used > MEMORY_THRESHOLDS.WARNING) {
        const alertEntry = {
          id: Date.now(),
          time: stats.timestamp,
          memory: used,
          redux: currentReduxSize,
          url: currentUrl,
          level: used > MEMORY_THRESHOLDS.CRITICAL ? 'critical' : 'warning',
        }
        addAlert(alertEntry)
      }
    } catch (error) {
      console.error('Memory check failed:', error)
    }
  }, [store, calculateReduxSize, addAlert, reduxSize])

  // Monitor memory usage
  useEffect(() => {
    if (!isAllowed) return
    if (typeof performance === 'undefined' || !performance.memory) return

    checkMemory()
    memoryCheckInterval.current = setInterval(checkMemory, CHECK_INTERVAL)

    return () => {
      if (memoryCheckInterval.current) {
        clearInterval(memoryCheckInterval.current)
      }
    }
  }, [checkMemory, isAllowed])

  // Drag handlers
  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault()
      setIsDragging(true)
      dragOffset.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      }
    },
    [position]
  )

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return

      setPosition({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      })
    },
    [isDragging]
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  useEffect(() => {
    if (!isDragging) return

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  const handleClick = useCallback(() => {
    if (!isDragging) setIsExpanded((prev) => !prev)
  }, [isDragging])

  const clearAlerts = useCallback(() => {
    setAlerts([])
  }, [])

  // Don't render unless allowed
  if (!isAllowed || !memoryStats)
    return null

  const getColorScheme = () => {
    if (memoryStats.used > MEMORY_THRESHOLDS.HIGH) return COLORS.CRITICAL
    if (memoryStats.used > MEMORY_THRESHOLDS.MEDIUM) return COLORS.WARNING
    return COLORS.NORMAL
  }

  const colorScheme = getColorScheme()
  const percentage = ((memoryStats.used / memoryStats.limit) * 100).toFixed(1)

  return (
    <div
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        zIndex: 9999,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: 12,
        cursor: isDragging ? 'grabbing' : 'default',
        pointerEvents: 'auto',
      }}
    >
      <div
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        style={{
          background: colorScheme.bg,
          color: colorScheme.text,
          padding: '10px 14px',
          borderRadius: 6,
          cursor: 'grab',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          userSelect: 'none',
          border: `2px solid ${colorScheme.border}`,
          fontWeight: 500,
          width: 'fit-content',
          transition: 'all 0.2s ease',
        }}
      >
        Memory: {memoryStats.used}MB ({percentage}%) | Redux: {reduxSize}MB
      </div>

      {isExpanded && (
        <div
          style={{
            background: '#ffffff',
            color: '#333',
            padding: 14,
            borderRadius: 6,
            marginTop: 8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            minWidth: 350,
            maxWidth: 500,
            maxHeight: 500,
            overflow: 'auto',
            border: '1px solid #e0e0e0',
          }}
        >
          <div style={{ fontSize: 13, marginBottom: 12 }}>
            <div>
              <strong>Used:</strong> {memoryStats.used}MB
            </div>
            <div>
              <strong>Total:</strong> {memoryStats.total}MB
            </div>
            <div>
              <strong>Limit:</strong> {memoryStats.limit}MB
            </div>
            <div>
              <strong>Redux:</strong> {reduxSize}MB
            </div>
            <div
              style={{
                fontSize: 11,
                wordBreak: 'break-all',
                color: '#666',
              }}
            >
              <strong>URL:</strong> {memoryStats.url}
            </div>
          </div>

          {alerts.length > 0 && (
            <div style={{ fontSize: 11 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <strong>Alerts ({alerts.length}):</strong>
                <button
                  onClick={clearAlerts}
                  style={{
                    background: '#f5f5f5',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    padding: '4px 8px',
                    fontSize: 10,
                    cursor: 'pointer',
                    color: '#666',
                  }}
                >
                  Clear
                </button>
              </div>
              <div style={{ maxHeight: 300, overflow: 'auto' }}>
                {alerts.map((a) => (
                  <div
                    key={a.id}
                    style={{
                      background:
                        a.level === 'critical'
                          ? COLORS.CRITICAL.bg
                          : COLORS.WARNING.bg,
                      borderLeft: `3px solid ${
                        a.level === 'critical'
                          ? COLORS.CRITICAL.border
                          : COLORS.WARNING.border
                      }`,
                      marginTop: 5,
                      padding: '6px 8px',
                      borderRadius: 4,
                    }}
                  >
                    <div style={{ fontWeight: 500, marginBottom: 2 }}>
                      {a.time}
                    </div>
                    <div style={{ color: '#666' }}>
                      Memory: {a.memory}MB | Redux: {a.redux}MB
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: '#999',
                        wordBreak: 'break-all',
                        marginTop: 2,
                      }}
                    >
                      {a.url}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default MemoryMonitor
